import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, map, mergeMap, of, switchMap } from 'rxjs';

import {
  LoginAsAnotherUserResponse,
  OrganizationsActions,
} from './organizations.actions';
import { OrganizationsService } from './organizations.service';

@Injectable()
export class OrganizationsEffects {
  private actions$ = inject(Actions);
  private organizationsService = inject(OrganizationsService);

  loadOrganizations$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OrganizationsActions.loadOrganizations),
      switchMap(() =>
        this.organizationsService.getOrganizations().pipe(
          map(response =>
            OrganizationsActions.loadOrganizationsSuccess({
              payload: response.res ?? [],
              meta: response.sts?.meta,
            })
          ),
          catchError(error =>
            of(OrganizationsActions.loadOrganizationsFailure({ error }))
          )
        )
      )
    )
  );

  getOrganizationDetails$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OrganizationsActions.getOrganizationDetails),
      switchMap(({ id }) =>
        this.organizationsService.getOrganizationDetails(id).pipe(
          map(response =>
            OrganizationsActions.getOrganizationDetailsSuccess({
              res: response.res,
            })
          ),
          catchError(error =>
            of(OrganizationsActions.getOrganizationDetailsFailure({ error }))
          )
        )
      )
    )
  );

  getOrganizationLicenses$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OrganizationsActions.getOrganizationLicenses),
      switchMap(() =>
        this.organizationsService.getOrganizationLicenses().pipe(
          map(response =>
            OrganizationsActions.getOrganizationLicensesSuccess({
              res: response.res ?? [],
            })
          ),
          catchError(error =>
            of(OrganizationsActions.getOrganizationLicensesFailure({ error }))
          )
        )
      )
    )
  );

  updateOrganizationLicenses$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OrganizationsActions.updateOrganizationLicenses),
      mergeMap(({ payload }) =>
        this.organizationsService.updateOrganizationLicense(payload).pipe(
          map(response =>
            OrganizationsActions.updateOrganizationLicensesSuccess({
              res: response.res,
            })
          ),
          catchError(error =>
            of(
              OrganizationsActions.updateOrganizationLicensesFailure({ error })
            )
          )
        )
      )
    )
  );

  deleteOrganizationAccount$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OrganizationsActions.deleteOrganizationAccount),
      mergeMap(({ acnId }) =>
        this.organizationsService.deleteOrganizationAccount(acnId).pipe(
          map(() =>
            OrganizationsActions.deleteOrganizationAccountSuccess({ acnId })
          ),
          catchError(error =>
            of(OrganizationsActions.deleteOrganizationAccountFailure({ error }))
          )
        )
      )
    )
  );

  updateOrganization$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OrganizationsActions.updateOrganization),
      mergeMap(({ id, testOrganizationFlag }) =>
        this.organizationsService
          .updateOrganization(id, testOrganizationFlag)
          .pipe(
            map(() =>
              OrganizationsActions.updateOrganizationSuccess({
                res: { testOrganizationFlag },
              })
            ),
            catchError(error =>
              of(OrganizationsActions.updateOrganizationFailure({ error }))
            )
          )
      )
    )
  );

  updateInvestorVisibility$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OrganizationsActions.updateInvestorVisibility),
      mergeMap(({ id, investorVisibility }) =>
        this.organizationsService
          .updateInvestorVisibility(id, investorVisibility)
          .pipe(
            map(() =>
              OrganizationsActions.updateInvestorVisibilitySuccess({
                res: { investorVisibility },
              })
            ),
            catchError(error =>
              of(
                OrganizationsActions.updateInvestorVisibilityFailure({
                  error,
                })
              )
            )
          )
      )
    )
  );

  updateFeatureStage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OrganizationsActions.updateFeatureStage),
      mergeMap(({ id, featureStage }) =>
        this.organizationsService.updateFeatureStage(id, featureStage).pipe(
          map(() =>
            OrganizationsActions.updateFeatureStageSuccess({
              res: { featureStage },
            })
          ),
          catchError(error =>
            of(
              OrganizationsActions.updateFeatureStageFailure({
                error,
              })
            )
          )
        )
      )
    )
  );

  updateOrganizationUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OrganizationsActions.updateOrganizationUser),
      mergeMap(({ userId, payload }) =>
        this.organizationsService.updateOrganizationUser(userId, payload).pipe(
          map(() =>
            OrganizationsActions.updateOrganizationUserSuccess({ userId })
          ),
          catchError(error =>
            of(OrganizationsActions.updateOrganizationUserFailure({ error }))
          )
        )
      )
    )
  );

  resetUserPassword$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OrganizationsActions.resetUserPassword),
      mergeMap(({ userId }) =>
        this.organizationsService.resetUserPassword(userId).pipe(
          map(response =>
            OrganizationsActions.resetUserPasswordSuccess({
              userId,
              message: response.res,
            })
          ),
          catchError(error =>
            of(OrganizationsActions.resetUserPasswordFailure({ error }))
          )
        )
      )
    )
  );

  resetUserPasswordLink$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OrganizationsActions.resetUserPasswordLink),
      mergeMap(({ userId }) =>
        this.organizationsService.resetUserPasswordLink(userId).pipe(
          map(response =>
            OrganizationsActions.resetUserPasswordLinkSuccess({
              userId,
              link: response.res,
            })
          ),
          catchError(error =>
            of(OrganizationsActions.resetUserPasswordLinkFailure({ error }))
          )
        )
      )
    )
  );

  generateOnBehalfToken$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OrganizationsActions.generateOnBehalfToken),
      mergeMap(({ userId }) =>
        this.organizationsService.generateOnBehalfUrl(userId).pipe(
          map((token: string) =>
            OrganizationsActions.generateOnBehalfTokenSuccess({ userId, token })
          ),
          catchError(error =>
            of(OrganizationsActions.generateOnBehalfTokenFailure({ error }))
          )
        )
      )
    )
  );

  loginAsAnotherUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OrganizationsActions.loginAsAnotherUser),
      mergeMap(({ token }) =>
        this.organizationsService.loginAsAnotherUserByToken(token).pipe(
          map((response: { res?: LoginAsAnotherUserResponse }) =>
            OrganizationsActions.loginAsAnotherUserSuccess({
              userData: response?.res,
            })
          ),
          catchError(error =>
            of(OrganizationsActions.loginAsAnotherUserFailure({ error }))
          )
        )
      )
    )
  );

  updateOrganizationName$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OrganizationsActions.updateOrganizationName),
      mergeMap(({ acnId, name }) =>
        this.organizationsService.updateOrganizationName(acnId, name).pipe(
          map(() =>
            OrganizationsActions.updateOrganizationNameSuccess({
              res: { companyName: name },
            })
          ),
          catchError(error =>
            of(OrganizationsActions.updateOrganizationNameFailure({ error }))
          )
        )
      )
    )
  );

  addOrganizationDomain$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OrganizationsActions.addOrganizationDomain),
      mergeMap(({ acnId, domain }) =>
        this.organizationsService.addOrganizationDomain(acnId, domain).pipe(
          map(response =>
            OrganizationsActions.addOrganizationDomainSuccess({
              res: response.res,
            })
          ),
          catchError(error =>
            of(OrganizationsActions.addOrganizationDomainFailure({ error }))
          )
        )
      )
    )
  );

  removeLoginBlock$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OrganizationsActions.removeLoginBlock),
      mergeMap(({ email }) =>
        this.organizationsService.removeLoginBlock(email).pipe(
          map(response =>
            OrganizationsActions.removeLoginBlockSuccess({
              email,
              message: response?.res,
            })
          ),
          catchError(error =>
            of(OrganizationsActions.removeLoginBlockFailure({ error }))
          )
        )
      )
    )
  );
}
